import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from '@battleops/localstore';
import { deptLearnModel, deptLearnWrites } from '@battleops/shared';
import { db } from './store';
import { commitWrites } from './commit';

// Department inference's learned layer (shared/deptinfer.js): meta/deptLearn = word → department counts from your own
// picks and corrections, private to you, synced to your devices. Read live; written one merge per pick.

/** { doc, model, learn(title, departmentId, { weight }) } — `learn` is stable and reads the latest doc. */
export function useDeptLearn() {
  const [learnDoc, setLearnDoc] = useState(null);
  const ref = useRef(null);
  useEffect(() => onSnapshot(doc(db, 'meta', 'deptLearn'),
    (snap) => { const d = snap.exists() ? snap.data() : null; ref.current = d; setLearnDoc(d); },
    () => { ref.current = null; setLearnDoc(null); }), []);
  const model = useMemo(() => deptLearnModel(learnDoc), [learnDoc]);
  const api = useMemo(() => ({
    learn: (title, departmentId, { weight = 1 } = {}) => {
      const writes = deptLearnWrites(ref.current, title, departmentId, { weight, now: Date.now() });
      if (!writes.length) return;
      // Seen at once by the next guess on this device, before the snapshot lands.
      const w = writes[0];
      ref.current = w.merge ? { ...(ref.current || {}), ...w.data } : { ...w.data };
      setLearnDoc(ref.current);
      commitWrites(writes).catch((e) => console.error('department learning failed:', e));
    },
  }), []);
  return { doc: learnDoc, model, learn: api.learn };
}
